import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Calendar, CheckCircle, Loader2, FileText, Target, Briefcase, Award, MessageSquare, TrendingUp, Zap, Shield, Eye, UserCheck, Linkedin, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import { DCA_LINKS } from '@/lib/dcaConfig';
import { publicApi } from '@/lib/api';

// Icon mapping
const iconMap = {
  FileText, Target, Briefcase, Award, MessageSquare, TrendingUp, Zap, Shield, Eye, UserCheck, Linkedin, Users, Calendar, CheckCircle, Search
};

const NewServicesPage = () => {
  const [paymentType, setPaymentType] = useState('oneTime');
  const [coachingServices, setCoachingServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  // Fetch coaching services and site settings from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesResponse, settingsResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coaching-services`),
          publicApi.getSiteSettings()
        ]);
        
        if (servicesResponse.ok) {
          const services = await servicesResponse.json();
          setCoachingServices(services || []);
        }
        
        setSettings(settingsResponse.data || {});
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Get icon component from name
  const getIcon = (iconName) => {
    return iconMap[iconName] || Briefcase;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title="Our Services" 
          breadcrumbs={[{ label: 'Services' }]} 
        />

        {/* Intro Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5" data-testid="services-intro">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                  Expert Career Support to Accelerate Your Success
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Whether you need a polished resume, LinkedIn optimization, interview coaching, or strategic career guidance, 
                  our team of industry professionals is here to help you achieve your goals.
                </p>
                <div className="flex flex-wrap gap-6 mb-8">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span>Industry Experts</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span>Proven Results</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span>Personalized Approach</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <a href="#services">View Services</a>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/workshops">
                      Workshops & Training <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-primary/10 to-accent/10">
                  <img
                    src="https://customer-assets.emergentagent.com/job_7df1c564-588e-4a17-acd3-02a3087015ac/artifacts/tf0utupu__DSA0689BSC.jpg"
                    alt="David Ashley - Career Strategist"
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary rounded-xl -z-10"></div>
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-accent rounded-xl -z-10"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Toggle */}
        <section className="py-8 bg-muted/50 border-y">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <span className="text-muted-foreground">Choose your payment option:</span>
              <div className="inline-flex bg-white rounded-full p-1 shadow-sm border">
                <button
                  onClick={() => setPaymentType('installment')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    paymentType === 'installment' 
                      ? 'bg-primary text-white shadow-md' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  $65 / Payment Plan
                </button>
                <button
                  onClick={() => setPaymentType('oneTime')}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    paymentType === 'oneTime' 
                      ? 'bg-primary text-white shadow-md' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  $250 One Payment
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Individual Coaching Services */}
        <section id="services" className="py-16" data-testid="coaching-services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Individual Coaching Services
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                One-on-one professional guidance tailored to your unique career needs.
                Book and manage your sessions through our CareerSupport.io platform.
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {coachingServices.map((service) => {
                  const IconComponent = getIcon(service.icon);
                  return (
                    <div 
                      key={service.id}
                      className="bg-white rounded-xl shadow-md border hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden group"
                      data-testid={`service-card-${service.id}`}
                    >
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {service.is_popular && (
                          <Badge className="absolute top-4 right-4 bg-accent">
                            <Star className="w-3 h-3 mr-1" /> Most Popular
                          </Badge>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                              <IconComponent className="w-5 h-5 text-primary" />
                            </div>
                            <h3 className="text-xl font-heading font-bold text-white">{service.title}</h3>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <p className="text-muted-foreground mb-4 line-clamp-2">
                          {service.short_description}
                        </p>

                        {/* Quick Stats */}
                        {service.stats && Object.keys(service.stats).length > 0 && (
                          <div className="flex items-center gap-4 mb-4 text-sm">
                            {Object.entries(service.stats).slice(0, 2).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-1 text-muted-foreground">
                                <CheckCircle className="w-4 h-4 text-primary" />
                                <span>{value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Pricing */}
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            {paymentType === 'oneTime' ? (
                              <span className="text-2xl font-bold text-primary">${service.pricing?.one_time || 250}</span>
                            ) : (
                              <div>
                                <span className="text-2xl font-bold text-primary">${service.pricing?.installment || 65}</span>
                                <span className="text-sm text-muted-foreground"> / {service.pricing?.installment_frequency || 'bi-weekly'}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions - Open inquiry modal for service */}
                        <div className="flex gap-3">
                          <Button 
                            className="flex-1" 
                            onClick={() => window.open(DCA_LINKS.general, '_blank', 'noopener,noreferrer')}
                            data-testid={`book-now-${service.slug}`}
                          >
                            Book Now <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                          <Button variant="outline" asChild>
                            <Link to={`/coaching/${service.slug}`}>
                              Details
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Workshops CTA */}
        <section className="py-16 bg-muted/30" data-testid="workshops-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-10 text-white">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
                    Looking for Team or Group Training?
                  </h2>
                  <p className="text-white/90 mb-6">
                    We offer customized workshops and training programs for organizations of all sizes. 
                    From career development to interview skills, we'll help your team succeed.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      <span>Groups of any size</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      <span>Flexible scheduling</span>
                    </div>
                  </div>
                  <Button 
                    size="lg" 
                    className="bg-accent hover:bg-accent/90"
                    asChild
                  >
                    <Link to="/workshops">
                      Explore Workshops <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </div>
                <div className="hidden lg:block">
                  <img
                    src="https://customer-assets.emergentagent.com/job_8056591d-682d-433c-8623-ad0e4f24d171/artifacts/r2bk86co_IMG-8395.jpg"
                    alt="Workshop training session"
                    className="rounded-xl shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16" data-testid="how-it-works">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                How It Works
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Getting started is easy. Here's what to expect when you book a service.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: 1, title: "Choose Your Service", description: "Select the coaching service that fits your career needs." },
                { step: 2, title: "Complete Intake", description: "Tell us about your background, goals, and what you're looking to achieve." },
                { step: 3, title: "Work with Experts", description: "Get personalized guidance from industry professionals." },
                { step: 4, title: "Achieve Results", description: "Land interviews, grow your network, and advance your career." }
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-heading font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-12 bg-secondary text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                  Ready to Accelerate Your Career?
                </h2>
                <p className="text-white/80">
                  Get expert guidance and support to achieve your professional goals.
                </p>
              </div>
              <div className="flex gap-4">
                <Button 
                  size="lg" 
                  className="bg-accent hover:bg-accent/90"
                  asChild
                >
                  <a href="#services">Get Started</a>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link to="/contact-us">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NewServicesPage;
